import React, { useState, useEffect, useCallback } from 'react';
import { Search, Star, Calendar, Loader2, AlertCircle, ArrowLeft, Clapperboard, MonitorPlay, ChevronDown, Filter, Globe, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY; 
const BASE_URL = 'https://api.themoviedb.org/3';
const IMAGE_URL = 'https://image.tmdb.org/t/p/w500';

// Professional Genre List from TMDB
const GENRES = [
  { id: '', name: 'All Categories' },
  { id: 28, name: 'Action' },
  { id: 878, name: 'Sci-Fi' },
  { id: 27, name: 'Horror' },
  { id: 35, name: 'Comedy' },
  { id: 18, name: 'Drama' },
  { id: 10749, name: 'Romance' }
];

const LANGUAGES = [
  { code: '', name: 'All Languages' },
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'Hindi' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'fr', name: 'French' }
];

const MovieSkeleton = () => (
  <div className="bg-slate-900/40 border border-white/5 rounded-[2.5rem] overflow-hidden animate-pulse">
    <div className="aspect-[16/10] bg-slate-800/50" />
    <div className="p-8 space-y-4">
      <div className="h-4 w-1/3 bg-slate-800 rounded-lg" />
      <div className="h-8 w-full bg-slate-800 rounded-lg" />
      <div className="h-20 w-full bg-slate-800/30 rounded-2xl" />
    </div>
  </div>
);

const MovieFinder = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
const [activeTab, setActiveTab] = useState('All Releases');  
  // Professional Filters
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedLang, setSelectedLang] = useState('');
  const [language, setLanguage] = useState(null); // null means 'All'
  const [page, setPage] = useState(1);

const fetchMovies = useCallback(async (isSearch = false) => {
  setLoading(true);
  setError(null);
  
  try {
    // 1. Determine Endpoint (Search vs Discover)
    const isQueryActive = isSearch && query.trim();
    const endpoint = isQueryActive ? '/search/movie' : '/discover/movie';
    
    // 2. Build Base URL with Dynamic Page
    let url = `${BASE_URL}${endpoint}?api_key=${TMDB_API_KEY}&include_adult=false&page=${page}`;

    // 3. Apply Search or Filters
    if (isQueryActive) {
      url += `&query=${encodeURIComponent(query)}`;
    } else {
      // These only work on the /discover endpoint
      url += `&sort_by=popularity.desc`;
      if (selectedGenre) url += `&with_genres=${selectedGenre}`;
      
      // Ensure this matches your state variable name (language or selectedLang)
      if (language) url += `&with_original_language=${language}`; 
    }

    const response = await fetch(url);
    if (!response.ok) throw new Error("Network response was not ok");
    
    const data = await response.json();
    
    if (data.results.length === 0) {
      setError("No titles match your current filters.");
      setResults([]);
    } else {
      // TMDB returns 20 items per page; slice as needed
      setResults(data.results); 
    }
  } catch (err) {
    setError("Data synchronization failed. Verify API connection.");
  } finally {
    setLoading(false);
  }
  // 4. Add 'page' and 'language' to the dependency array
}, [query, selectedGenre, language, page]);

  useEffect(() => {
    // Only fetch trending/discover if not actively typing in search
    if (!query) fetchMovies();
  }, [selectedGenre, selectedLang, fetchMovies]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMovies(true);
  };

// Helper for TMDB Images
const getImageUrl = (path, size = 'w500') => {
  if (!path) return "https://via.placeholder.com/500x750?text=Movie+Directly"; // Fallback
  return `https://image.tmdb.org/t/p/${size}${path}`;
};

// Sort all results by date (Latest first)

// 1. Sort by date
const sorted = [...results].sort((a, b) => 
  new Date(b.release_date || b.first_air_date) - new Date(a.release_date || a.first_air_date)
);

// 2. Find the LATEST movie that actually has an image for the Feature Card
const featured = sorted.find(item => item.backdrop_path) || sorted[0];

// 3. Filter out the featured item from the rest of the grid
const gridItems = sorted.filter(item => item.id !== featured.id).slice(0, 14);

// 1. ENSURE YOUR USEEFFECT LOOKS LIKE THIS
useEffect(() => {
  // We pass true if there is a query to tell the function to use the search endpoint
  const delayDebounceFn = setTimeout(() => {
    fetchMovies(query.length > 0);
  }, 500); // 500ms delay to prevent API spam while typing

  return () => clearTimeout(delayDebounceFn);
}, [query, selectedLang, selectedGenre, page]);

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 font-sans pb-20 selection:bg-cyan-500/30">
      
       <Helmet>
      {/* Page Title */}
      <title>MovieDirectly | Find Movies & TV Series Across Global Platforms</title>

      {/* Primary Meta Tags */}
      <meta
        name="description"
        content="MovieDirectly by MoreTools: Search over 1.2M+ movies and TV series globally. Discover where to watch, check IMDB ratings, and explore trending shows on Netflix, Prime Video, Disney+, and more."
      />
      <meta
        name="keywords"
        content="MovieDirectly, Movie search, TV series search, global streaming, Netflix, Prime Video, Disney+, Omkar More, MoreTools, movie finder, streaming guide, IMDB ratings"
      />
      <meta name="author" content="Omkar More | MoreTools Network" />

      {/* Open Graph / Social Media */}
      <meta property="og:title" content="MovieDirectly | Global Movie & TV Finder" />
      <meta
        property="og:description"
        content="Search and discover movies and TV series across 500+ platforms with MovieDirectly. Powered by TMDB and MoreTools Network."
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://omkarmore.com/moviedirectly" />
      <meta property="og:image" content="https://image.tmdb.org/t/p/original/YOUR_FEATURED_IMAGE.jpg" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="MovieDirectly | Global Movie & TV Finder" />
      <meta
        name="twitter:description"
        content="Discover movies and TV series worldwide with MovieDirectly. Powered by TMDB and MoreTools Network."
      />
      <meta name="twitter:image" content="https://image.tmdb.org/t/p/original/YOUR_FEATURED_IMAGE.jpg" />

      {/* Canonical URL */}
      <link rel="canonical" href="https://omkarmore.com/moviedirectly" />

      {/* Optional: Favicon */}
      <link rel="icon" href="/favicon.ico" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {`
        {
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "MovieDirectly",
          "url": "https://omkarmore.com/moviedirectly",
          "applicationCategory": "Entertainment",
          "author": {
            "@type": "Person",
            "name": "Omkar More"
          },
          "description": "MovieDirectly by MoreTools: Search over 1.2M+ movies and TV series globally. Discover where to watch, check IMDB ratings, and explore trending shows on Netflix, Prime Video, Disney+, and more.",
          "operatingSystem": "Web",
          "sameAs": [
            "https://twitter.com/yourtwitter",
            "https://www.facebook.com/yourfacebook",
            "https://www.linkedin.com/in/omkarmore"
          ]
        }
        `}
      </script>
    </Helmet>

      {/* 1. BRANDED NAVBAR */}
  {/* 1. BRANDED NAVBAR */}

{/* 1. BRANDED NAVBAR */}
<nav className="sticky top-0 z-[100] border-b border-white/5 bg-[#020617]/70 backdrop-blur-xl transition-all duration-300">
  <div className="max-w-[1600px] mx-auto px-4 md:px-12 h-20 flex justify-between items-center">
    
    {/* Left Side: Consolidated Brand Hierarchy */}
    <div className="flex items-center gap-3 md:gap-8">
      <Link 
        to="/tools" 
        className="group flex items-center gap-3 px-3 md:px-5 py-2.5 bg-white/[0.03] border border-white/5 rounded-2xl hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-all"
      >
        <ArrowLeft size={16} className="text-slate-400 group-hover:text-cyan-500 group-hover:-translate-x-1 transition-all" />
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 group-hover:text-white transition-colors hidden lg:block">
          Back to Tools
        </span>
      </Link>

      <div className="h-10 w-[1px] bg-white/5 hidden sm:block" />

      <div className="flex flex-col">
        <span className="text-[14px] md:text-[16px] font-black uppercase tracking-[0.3em] text-white leading-none">
          MovieDirectly
        </span>
        <div className="flex items-center gap-2 mt-2">
            <span className="text-[8px] font-black uppercase tracking-[0.2em] text-slate-500">
                MoreTools <span className="text-white/10 mx-1">|</span> <span className="text-cyan-500/80">Directly.Network</span>
            </span>
        </div>
      </div>
    </div>
    
    {/* Right Side: Minimalist Architect Signature */}
{/* Right Side: Brand Mark / Logo */}
{/* Right Side: Clickable Architect Identity */}
<a 
  href="https://omkarmore.com" 
  target="_blank" 
  rel="noopener noreferrer" 
  className="flex items-center gap-4 md:gap-6 group"
>
  
  {/* 1. TEXT SIGNATURE: Hidden on Mobile, Flex on Desktop */}
  <div className="hidden md:flex flex-col items-end text-right pr-6 border-r border-white/5 transition-colors group-hover:border-cyan-500/30">
    <span className="text-[7px] font-black text-slate-600 uppercase tracking-[0.4em] mb-0.5 group-hover:text-cyan-500 transition-colors">
      Developed By
    </span>
    <span className="text-[9px] font-bold text-white uppercase tracking-widest opacity-80 group-hover:opacity-100 transition-opacity">
      OMKAR MORE
    </span>
  </div>

  {/* 2. LOGO MARK: Visible on all screens */}
  <div className="relative">
    {/* Animated Glow Effect */}
    <div className="absolute inset-0 bg-cyan-500/20 blur-md rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 scale-110" />
    
    <div className="relative w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden group-hover:border-cyan-500/50 group-hover:bg-cyan-500/5 transition-all duration-300">
      <img 
        src="/path-to-your-logo.png" 
        alt="Omkar More Logo" 
        className="w-6 h-6 md:w-7 md:h-7 object-contain opacity-70 group-hover:opacity-100 transition-all duration-300"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "https://www.google.com/s2/favicons?sz=64&domain=omkarmore.com"; 
        }}
      />
    </div>
  </div>
</a>

  </div>
</nav>

{/* 2. HIGH-DENSITY VISUAL DISCOVERY */}
<header className="relative w-full min-h-screen bg-[#020617] pt-5 pb-12 overflow-hidden">
  
  {/* Cinematic Ambient Lighting */}
  <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-cyan-500/5 blur-[120px] pointer-events-none" />
  <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-500/5 blur-[120px] pointer-events-none" />

  <div className="max-w-[1600px] mx-auto px-6 md:px-12 relative z-10 space-y-10">
    
    {/* Clean Section Header */}
    <div className="flex items-center justify-between border-b border-white/5 pb-6">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
          <span className="text-[10px] font-black uppercase tracking-[0.4em] text-cyan-500">Global Premiere Stream</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-black text-white uppercase italic tracking-tighter">
          Latest Releases <span className="text-slate-600">/</span> Incoming
        </h2>
      </div>
      
      <div className="text-right hidden sm:block">
        <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Auto-Updating Feed</p>
        <p className="text-[10px] font-bold text-white uppercase">{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
      </div>
    </div>

    {/* PROPER RESPONSIVE GRID */}
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4 md:gap-5">
      
      {/* 1. Feature Premiere Card */}
      {featured && (
        <div className="col-span-2 row-span-2 group relative rounded-[2.5rem] overflow-hidden border border-white/10 bg-[#0a0f1d] shadow-2xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-[#020617] to-[#020617]" />
          
          <img 
            src={featured.backdrop_path 
              ? `https://image.tmdb.org/t/p/original${featured.backdrop_path}` 
              : `https://image.tmdb.org/t/p/original${featured.poster_path}`
            } 
            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 opacity-50"
            alt={featured.title || featured.name}
            onError={(e) => { e.target.style.display = 'none'; }}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#020617]/60 via-transparent to-transparent" />

          <div className="absolute bottom-0 left-0 p-10 space-y-6 w-full">
            <div className="flex items-center gap-3">
               <span className="px-3 py-1 bg-cyan-500 text-black text-[9px] font-black uppercase tracking-widest rounded-full animate-pulse">
                 Coming Soon
               </span>
               <span className="text-white/40 text-[9px] font-black uppercase tracking-[0.2em]">
                 Expected March 26
               </span>
            </div>

            <h3 className="text-4xl md:text-5xl font-black text-white uppercase italic leading-[0.85] tracking-tighter">
              {featured.title || featured.name}
            </h3>

            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-6">
                <div className="flex flex-col">
                  <span className="text-slate-500 text-[8px] font-black uppercase tracking-widest">Rating</span>
                  <span className="text-white text-sm font-black italic">TBA</span>
                </div>
                <div className="w-[1px] h-8 bg-white/10" />
                <div className="flex flex-col">
                  <span className="text-slate-500 text-[8px] font-black uppercase tracking-widest">Status</span>
                  <span className="text-cyan-500 text-sm font-black italic uppercase">In Production</span>
                </div>
              </div>

              {/* ACTION: Play Trailer */}
              <button 
                onClick={() => window.open(`https://www.youtube.com/results?search_query=${featured.title || featured.name}+official+trailer`, '_blank')}
                className="flex items-center gap-3 px-6 py-3 bg-white text-black rounded-2xl hover:bg-cyan-400 transition-all active:scale-95 group/btn"
              >
                <svg className="w-3 h-3 fill-black" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                <span className="text-[10px] font-black uppercase tracking-widest">Play Trailer</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. High-Density Poster Grid */}
{/* 2. High-Density Poster Grid */}
{gridItems.map((item, idx) => (
  <div 
    key={item.id} 
    className="group relative aspect-[2/3] rounded-2xl overflow-hidden border border-white/5 bg-[#0a0f1d] hover:border-cyan-500/40 transition-all duration-500"
  >
    <img 
      src={getImageUrl(item.poster_path)} 
      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      alt={item.title || item.name}
      loading="lazy"
      onError={(e) => {
        e.target.onerror = null; 
        e.target.src = "https://via.placeholder.com/500x750?text=No+Poster+Available";
      }}
    />
    
    {/* Detailed Info Overlay */}
    <div className="absolute inset-0 bg-[#020617]/95 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-5 space-y-5">
      
      {/* DIRECT STREAMING ACCESS */}
      <div className="space-y-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
        <span className="text-[7px] font-black text-slate-500 uppercase tracking-[0.3em]">Stream Now On</span>
        
        <div className="flex flex-wrap gap-2">
          {/* Logic: Mapping through providers if they exist, otherwise showing primary fallbacks */}
          {[
            { id: 'nflx', domain: 'netflix.com', url: `https://www.netflix.com/search?q=${item.title || item.name}` },
            { id: 'amzn', domain: 'primevideo.com', url: `https://www.primevideo.com/search?phrase=${item.title || item.name}` },
            { id: 'yt', domain: 'youtube.com', url: `https://www.youtube.com/results?search_query=${item.title || item.name}+full+movie` }
          ].map((provider) => (
            <a 
              key={provider.id}
              href={provider.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-2.5 py-1.5 bg-white/[0.03] border border-white/10 rounded-lg hover:bg-white/10 hover:border-cyan-500/50 transition-all active:scale-90"
            >
              <img 
                src={`https://www.google.com/s2/favicons?sz=32&domain=${provider.domain}`} 
                className="w-3.5 h-3.5 object-contain" 
                alt={provider.id} 
              />
            </a>
          ))}
        </div>
      </div>

      {/* MOVIE TITLE & INFO */}
      <div className="space-y-1 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300 delay-75">
        <h4 className="text-[12px] font-black text-white uppercase leading-tight line-clamp-2">
          {item.title || item.name}
        </h4>
        <div className="flex items-center justify-between border-t border-white/5 pt-2">
           <p className="text-[9px] font-bold text-cyan-500 uppercase">{item.release_date?.split('-')[0] || 'TBA'}</p>
           <div className="flex items-center gap-2">
             <span className="text-[8px] text-slate-500 font-black uppercase">{(item.media_type || 'Movie')}</span>
             <div className="w-1 h-1 rounded-full bg-slate-700" />
             <span className="text-[8px] text-slate-500 font-black uppercase italic">HD</span>
           </div>
        </div>
      </div>

      {/* QUICK ACTIONS */}
      <div className="grid grid-cols-2 gap-2 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300 delay-150">
        <button 
          onClick={(e) => { e.stopPropagation(); window.open(`https://www.youtube.com/results?search_query=${item.title || item.name}+trailer`, '_blank'); }}
          className="py-2.5 bg-white/5 border border-white/10 text-white text-[8px] font-black uppercase tracking-widest rounded-xl hover:bg-white/10 transition-all"
        >
          Trailer
        </button>
        <button 
          onClick={() => window.open(`https://www.themoviedb.org/movie/${item.id}`, '_blank')}
          className="py-2.5 bg-cyan-500 text-black text-[8px] font-black uppercase tracking-widest rounded-xl active:scale-95 transition-all shadow-[0_0_20px_rgba(6,182,212,0.3)]"
        >
          Details
        </button>
      </div>
    </div>

    {/* Logic-Based Badges */}
    {idx < 2 && (
      <div className="absolute top-3 left-3 px-2 py-0.5 bg-white text-[#020617] text-[8px] font-black uppercase rounded shadow-xl z-20">
        New Release
      </div>
    )}
  </div>
))}
    </div>

    {/* 4. STREAMING ECOSYSTEM & HUB STATUS */}
    <footer className="space-y-12 pt-20 pb-12 border-t border-white/5">
      <div className="flex flex-col items-center gap-8 px-4">
        <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] text-center">
          Available on Premium Networks
        </h3>
        
        <div className="flex flex-wrap justify-center items-center gap-y-10 gap-x-8 md:gap-x-12 lg:gap-x-16 w-full max-w-7xl px-4 mx-auto">
          {[
            { name: 'Netflix', domain: 'netflix.com' },
            { name: 'Prime Video', domain: 'primevideo.com' },
            { name: 'Disney+', domain: 'disneyplus.com' },
            { name: 'Apple TV+', domain: 'tv.apple.com' },
            { name: 'Hulu', domain: 'hulu.com' },
            { name: 'HBO Max', domain: 'max.com' },
            { name: 'Paramount+', domain: 'paramountplus.com' },
            { name: 'YouTube', domain: 'youtube.com' },
            { name: 'JioCinema', domain: 'jiocinema.com' },
            { name: 'SonyLIV', domain: 'sonyliv.com' },
            { name: 'Zee5', domain: 'zee5.com' },
            { name: 'Hotstar', domain: 'hotstar.com' }
          ].map((brand) => (
            <a key={brand.name} href={`https://${brand.domain}`} target="_blank" rel="noopener noreferrer" className="group flex flex-col items-center gap-3 active:scale-90 transition-transform duration-200">
              <div className="relative p-3.5 rounded-2xl bg-white/[0.04] border border-white/10 shadow-2xl">
                <img src={`https://www.google.com/s2/favicons?sz=64&domain=${brand.domain}`} alt={brand.name} className="w-6 h-6 md:w-8 md:h-8 object-contain block brightness-110" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">{brand.name}</span>
            </a>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-10 bg-white/[0.02] border border-white/5 p-8 md:p-12 rounded-[2.5rem] md:rounded-[4rem]">
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 sm:gap-12 md:gap-20 w-full lg:w-auto">
            <div className="space-y-1 text-center sm:text-left">
              <p className="text-2xl md:text-3xl font-black text-white tracking-tight">850k+</p>
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">Total Movies</p>
            </div>
            <div className="space-y-1 text-center sm:text-left">
              <p className="text-2xl md:text-3xl font-black text-cyan-500 tracking-tight">1.2M+</p>
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">Data Points</p>
            </div>
            <div className="col-span-2 sm:col-span-1 space-y-1 text-center sm:text-left">
              <p className="text-2xl md:text-3xl font-black text-white tracking-tight">Live</p>
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">Update Stream</p>
            </div>
          </div>

          <div className="flex flex-col items-center lg:items-end gap-8 w-full lg:w-auto border-t lg:border-t-0 border-white/5 pt-10 lg:pt-0">
            <div className="flex items-center gap-2.5 bg-green-500/5 px-5 py-2 rounded-full border border-green-500/20">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[10px] font-black text-green-500 uppercase tracking-widest">System Online</span>
            </div>
            
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <div className="flex flex-col items-center md:items-end gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-black text-white uppercase tracking-[0.3em]">MovieDirectly</span>
                  <div className="h-3 w-px bg-white/10 hidden md:block" />
                  <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">by Omkar More</span>
                </div>
                <span className="text-[8px] font-black text-cyan-500/60 uppercase tracking-[0.4em]">Part of MoreTools Network</span>
              </div>

              <div className="hidden md:block h-8 w-px bg-white/5" />

              <div className="flex items-center gap-4 bg-white/[0.03] px-5 py-3 rounded-2xl border border-white/10 shadow-2xl backdrop-blur-sm">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-[0.2em] whitespace-nowrap">Powered by</span>
                <a href="https://www.themoviedb.org/" target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-all active:scale-95">
                  <img src="https://www.themoviedb.org/assets/2/v4/logos/v2/blue_short-8e7b30f73a4020692ccca9c88bafe5dcb6f8a62a4c6bc55cd9ba82bb2cd95f6c.svg" className="h-3.5 md:h-4 w-auto object-contain brightness-125" alt="TMDB" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</header>

      {/* 3. DYNAMIC CONTENT GRID */}
{/* 3. DYNAMIC CONTENT ENGINE */}
{/* 3. DYNAMIC CONTENT ENGINE */}
{/* 3. DYNAMIC CONTENT ENGINE */}
<main className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 space-y-12">
  
  {/* PRO ACTION BAR */}
  <div className="flex flex-col gap-8 bg-white/[0.02] border border-white/5 p-6 md:p-8 rounded-[3rem] backdrop-blur-3xl">
    
    {/* 1. Search Bar */}
    <div className="relative w-full group">
      <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-cyan-500 transition-colors" size={20} />
      <input 
        type="text"
        placeholder="Search Hollywood, Bollywood, or Global Cinema..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setPage(1); // Reset to page 1 on search
        }}
        className="w-full bg-black/40 border border-white/10 rounded-[2rem] py-5 pl-16 pr-8 text-sm font-bold text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 transition-all"
      />
    </div>

    {/* 2. Responsive Filter Cloud (No horizontal scroll) */}
{/* PRO ACTION BAR: Dropdown Edition */}
<div className="flex flex-col lg:flex-row gap-4 bg-white/[0.02] border border-white/5 p-4 rounded-[2rem] backdrop-blur-3xl items-center">
  


  {/* 2. Selection Controls */}
  <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
    
    {/* Regional Dropdown */}
    <div className="relative group/select w-full sm:w-48">
      <select 
        value={selectedLang || ""}
        onChange={(e) => { setSelectedLang(e.target.value || null); setPage(1); }}
        className="w-full appearance-none bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-[10px] font-black uppercase tracking-widest text-white cursor-pointer hover:bg-white/10 hover:border-white/20 transition-all outline-none"
      >
        <option value="" className="bg-[#020617]">All Regions</option>
        <option value="en" className="bg-[#020617]">Hollywood</option>
        <option value="hi" className="bg-[#020617]">Bollywood</option>
        <option value="ko" className="bg-[#020617]">K-Drama</option>
        <option value="ja" className="bg-[#020617]">Anime</option>
      </select>
      <ChevronDown size={14} className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none group-hover/select:text-white transition-colors" />
    </div>

    {/* Genre Dropdown */}
    <div className="relative group/select w-full sm:w-56">
      <select 
        value={selectedGenre || ""}
        onChange={(e) => { setSelectedGenre(e.target.value || null); setPage(1); }}
        className="w-full appearance-none bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-[10px] font-black uppercase tracking-widest text-white cursor-pointer hover:bg-white/10 hover:border-white/20 transition-all outline-none"
      >
        <option value="" className="bg-[#020617]">All Genres</option>
        {GENRES.map((genre) => (
          <option key={genre.id} value={genre.id} className="bg-[#020617]">
            {genre.name}
          </option>
        ))}
      </select>
      <ChevronDown size={14} className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none group-hover/select:text-white transition-colors" />
    </div>

    {/* Reset Filter Button (Optional but useful) */}
    {(selectedLang || selectedGenre || query) && (
      <button 
        onClick={() => { setQuery(""); setSelectedLang(null); setSelectedGenre(null); setPage(1); }}
        className="px-6 py-4 bg-cyan-500/10 border border-cyan-500/20 rounded-2xl text-[9px] font-black uppercase text-cyan-500 hover:bg-cyan-500 hover:text-black transition-all whitespace-nowrap"
      >
        Reset Grid
      </button>
    )}
  </div>
</div>
  </div>

  {/* CONTENT FEED HEADER */}
  <div className="flex items-center gap-6">
    <div className="flex items-center gap-3">
      <div className="w-2 h-2 rounded-full bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.5)]" />
      <h2 className="text-[11px] font-black text-white uppercase tracking-[0.4em]">
        {query ? 'Processing Search' : 'Global Feed'}
      </h2>
    </div>
    <div className="h-px bg-gradient-to-r from-white/10 to-transparent flex-1" />
  </div>

  {/* GRID SYSTEM */}
{/* GRID SYSTEM */}
{error ? (
  <div className="flex flex-col items-center justify-center py-32 text-center bg-white/[0.01] rounded-[3rem] border border-dashed border-white/5">
    <AlertCircle size={40} className="text-slate-800 mb-4" />
    <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest">{error}</p>
  </div>
) : (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4 md:gap-8">
    {loading 
      ? Array(14).fill(0).map((_, i) => (
          <div key={i} className="aspect-[2/3] bg-white/5 animate-pulse rounded-2xl" />
        ))
      : results.map((movie) => (
          <div 
            key={movie.id} 
            className="group relative aspect-[2/3] rounded-2xl overflow-hidden border border-white/5 bg-[#0a0f1d] hover:border-cyan-500/40 transition-all duration-500"
          >
            <img 
              src={movie.poster_path 
                ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` 
                : "https://via.placeholder.com/500x750?text=Poster+Pending"} 
              alt={movie.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              loading="lazy"
            />

            {/* Cinematic Overlay */}
            <div className="absolute inset-0 bg-[#020617]/95 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-6 space-y-5">
              
              {/* 1. DIRECT STREAMING ACCESS */}
              <div className="space-y-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                <span className="text-[7px] font-black text-slate-500 uppercase tracking-[0.3em]">Stream Now On</span>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'nflx', domain: 'netflix.com', url: `https://www.netflix.com/search?q=${movie.title || movie.name}` },
                    { id: 'amzn', domain: 'primevideo.com', url: `https://www.primevideo.com/search?phrase=${movie.title || movie.name}` },
                    { id: 'yt', domain: 'youtube.com', url: `https://www.youtube.com/results?search_query=${movie.title || movie.name}+full+movie` }
                  ].map((provider) => (
                    <a 
                      key={provider.id}
                      href={provider.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-2.5 py-1.5 bg-white/[0.03] border border-white/10 rounded-lg hover:bg-white/10 hover:border-cyan-500/50 transition-all active:scale-90"
                    >
                      <img 
                        src={`https://www.google.com/s2/favicons?sz=32&domain=${provider.domain}`} 
                        className="w-3.5 h-3.5 object-contain" 
                        alt={provider.id} 
                      />
                    </a>
                  ))}
                </div>
              </div>

              {/* 2. RATINGS & METADATA */}
              <div className="space-y-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-75">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 bg-cyan-500/10 px-2 py-1 rounded-md border border-cyan-500/20">
                    <Star size={10} className="text-cyan-500 fill-cyan-500" />
                    <span className="text-[10px] font-black text-white">{movie.vote_average?.toFixed(1) || '0.0'}</span>
                  </div>
                  <span className="text-[8px] font-black text-slate-500 uppercase px-1.5 py-1 border border-slate-800 rounded">
                    {movie.original_language === 'hi' ? 'Bollywood' : 'Hollywood'}
                  </span>
                </div>
                <h4 className="text-[13px] font-black text-white uppercase leading-tight line-clamp-2">
                  {movie.title || movie.name}
                </h4>
              </div>
              
              {/* 3. GRID ACTIONS */}
              <div className="grid grid-cols-2 gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-150">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`https://www.youtube.com/results?search_query=${movie.title || movie.name}+trailer`, '_blank');
                  }}
                  className="py-2.5 bg-white/5 border border-white/10 text-white text-[8px] font-black uppercase tracking-widest rounded-xl hover:bg-white/10 transition-all"
                >
                  Trailer
                </button>
                <button 
                  onClick={() => window.open(`https://www.themoviedb.org/movie/${movie.id}`, '_blank')}
                  className="py-2.5 bg-white text-black text-[8px] font-black uppercase tracking-widest rounded-xl active:scale-95 transition-all"
                >
                  Details
                </button>
              </div>
            </div>

            {/* Year Badge */}
            <div className="absolute top-4 right-4 px-2 py-1 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg text-[8px] font-black text-slate-400">
              {movie.release_date?.split('-')[0] || 'TBA'}
            </div>
          </div>
        ))
    }
  </div>
)}

  {/* PAGINATION SYSTEM (1 TO 10) */}
  {!loading && !error && results.length > 0 && (
    <div className="flex flex-col items-center gap-8 pt-12 border-t border-white/5">
      <div className="flex flex-wrap justify-center items-center gap-2 md:gap-3">
        {[...Array(10)].map((_, i) => {
          const pageNum = i + 1;
          return (
            <button
              key={pageNum}
              onClick={() => {
                setPage(pageNum);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className={`w-10 h-10 md:w-12 md:h-12 rounded-xl text-[10px] font-black transition-all flex items-center justify-center border ${
                page === pageNum
                ? 'bg-cyan-500 text-black border-cyan-500 shadow-lg shadow-cyan-500/20'
                : 'bg-white/5 text-slate-500 border-white/5 hover:border-white/20 hover:text-white'
              }`}
            >
              {pageNum.toString().padStart(2, '0')}
            </button>
          );
        })}
      </div>
      
      <p className="text-[9px] font-black text-slate-600 uppercase tracking-[0.4em]">
        Data Stream Analysis — Page {page} of 10
      </p>
    </div>
  )}
</main>

      {/* 4. FOOTER COMPLIANCE */}
{/* 4. COMPLIANT & BRANDED FOOTER */}
<footer className="w-full bg-[#020617] text-white border-t border-white/5 pt-24 pb-12 px-6">
  <div className="max-w-7xl mx-auto flex flex-col items-center gap-16">

    {/* Section 1: Technical Infrastructure Partners */}
    <div className="flex flex-col items-center gap-6">
      <span className="text-[9px] font-black text-slate-600 uppercase tracking-[0.4em]">Engine Infrastructure</span>
      <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 opacity-40 hover:opacity-100 transition-all duration-500">
        <img 
          src="https://www.gstatic.com/images/branding/googlelogo/2x/googlelogo_light_color_92x30dp.png" 
          alt="Google Cloud" 
          className="h-5 object-contain"
        />
        <div className="h-4 w-px bg-white/10" />
        <img 
          src="https://www.themoviedb.org/assets/2/v4/logos/v2/blue_short-8e7b30f73a4020692ccca9c88bafe5dcb6f8a62a4c6bc55cd9ba82bb2cd95f6c.svg" 
          alt="TMDB" 
          className="h-5 object-contain brightness-125"
        />
      </div>
    </div>

    {/* Section 2: Core Platform Branding */}
    <div className="text-center space-y-6 max-w-3xl">
      <div className="flex flex-col items-center gap-2">
        <h2 className="text-xl md:text-2xl font-black tracking-tighter uppercase">
          Movie<span className="text-cyan-500">Directly</span>
        </h2>
        <div className="h-px w-12 bg-cyan-500/50" />
      </div>

    
 <p className="text-[10px] md:text-[11px] text-slate-500 font-bold uppercase tracking-[0.18em] leading-[2] max-w-2xl mx-auto text-center [text-wrap:balance]">
  <span className="text-white">MovieDirectly</span> is a professional online tool within the 
  <span className="text-white"> MoreTools Network</span>, developed and maintained by 
  <span className="text-white"> Omkar More</span>. 
  It provides accurate cinema and media metadata, powered by the   <span className="text-white"> TMDB</span> API, and is part of a 
  growing ecosystem of tools designed for engineers, developers, and professionals 
  across multiple domains.
</p>
    </div>

    {/* Section 3: Recommended Directly Websites */}
    <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
      {[
        { name: 'JobDirectly', url: 'https://jobdirectly.com', logo: "https://jobdirectly.com/static/media/JD_Logo.9d693e5593d7d89a9bc4.png" },
        { name: 'StudentDirectly', url: 'https://studentdirectly.com', logo: 'https://jobdirectly.com/static/media/Student_D_Logoo.92a4e5069fea7caad713.jpeg' },
        { name: 'MoreTools', url: 'https://omkarmore.com/tools', logo: 'https://omkarmore.com/static/media/LogoOM.281737d6397aef0d61c5.PNG' }
      ].map((site) => (
        <a 
          key={site.name}
          href={site.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center gap-2 group transition-transform hover:scale-105"
        >
          <img 
            src={site.logo} 
            alt={site.name} 
            className="h-10 w-auto object-contain"
          />
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-white transition-colors">
            {site.name}
          </span>
        </a>
      ))}
    </div>

    {/* Section 4: Legal & Copyright */}
<div className="w-full pt-12 mt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-10">

  {/* Left Side: Identity & Status */}
  <div className="flex flex-col items-center md:items-start gap-3">
    <div className="flex flex-wrap justify-center md:justify-start items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-white">
      <span>© 2026 Omkar More</span>
      <span className="text-slate-800 hidden md:block">|</span>
      <span className="text-cyan-500/90">Directly.Network Architecture</span>
    </div>
    
    {/* Status Indicator */}

  </div>

  {/* Right Side: Compliance & Infrastructure */}
  <div className="flex flex-col items-center md:items-end gap-2">
    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest text-center md:text-right max-w-[320px] leading-relaxed">
      This product uses the TMDB API but is not endorsed or certified by TMDB. 
    </p>
    <div className="flex items-center gap-2 px-3 py-1 rounded-md bg-white/[0.02] border border-white/5">
       <span className="text-[8px] text-slate-700 font-black uppercase tracking-widest">
         Powered by Google Cloud Platform
       </span>
    </div>
  </div>
</div>

  </div>
</footer>
    </div>
  );
};

export default MovieFinder;